export interface LatLng {
  lat: number;
  lng: number;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Validates and returns a normalized lat/lng pair from the venue location payload.
 *
 * The web app now expects location values from the API to already be in
 * `{ lat, lng }` format. Invalid or incomplete values return null.
 */
export const parseVenueLocation = (value: unknown): LatLng | null => {
  if (!value || typeof value !== 'object') return null;

  const maybeLatLng = value as Partial<LatLng>;
  if (!isFiniteNumber(maybeLatLng.lat) || !isFiniteNumber(maybeLatLng.lng)) {
    return null;
  }

  return { lat: maybeLatLng.lat, lng: maybeLatLng.lng };
};

/**
 * Builds the map query string used by embedded map views.
 *
 */
export const buildVenueMapQuery = (
  locationValue: unknown,
  fallbackAddress?: string | null,
): string | null => {
  const coordinates = parseVenueLocation(locationValue);
  if (coordinates) {
    return `${coordinates.lat},${coordinates.lng}`;
  }

  const address = fallbackAddress?.trim();
  return address ? address : null;
};

/**
 * Creates a Google Maps embed URL from a query string.
 *
 * @param query A coordinate pair ("lat,lng") or an address string.
 * @param zoom Optional map zoom level. Defaults to 15.
 */
export const buildGoogleMapsEmbedUrl = (query: string, zoom = 15): string =>
  `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed`;
